import { useMemo, useCallback, useEffect } from 'react'
import {
  StyleClipData,
  StyleClipItem,
  VideoItem,
  VideoAPIResponse,
} from '../types'
import useGetGalleryVideos from '../../../../hooks/query/useGetGalleryVideos'
import { useVideoPolling } from '../../../../hooks/useVideoPolling'
import { downloadFile, downloadMultipleFiles } from '../../../../utils/download'

interface UseStyleClipDataProps {
  page: number
  itemsPerPage: number
  enabled?: boolean
}

// API 상태를 갤러리 상태로 매핑하는 함수
const mapAPIStatusToGalleryStatus = (
  status: string
): 'queued' | 'processing' | 'completed' | 'failed' => {
  switch (status.toLowerCase()) {
    case 'queued':
      return 'queued'
    case 'processing':
      return 'processing'
    case 'completed':
      return 'completed'
    case 'failed':
      return 'failed'
    default:
      return 'queued'
  }
}

// 비디오 데이터를 StyleClipItem으로 변환하는 함수
const transformVideoDataToStyleClipItems = (
  videos: VideoItem[]
): StyleClipItem[] => {
  return videos.map((video) => ({
    id: video.id.toString(),
    type: 'style-clip' as const,
    imageUrl: video.thumbnailUrl,
    videoUrl: video.resultVideoUrl,
    thumbnailUrl: video.thumbnailUrl,
    status: mapAPIStatusToGalleryStatus(video.status),
    createdAt: video.createdAt,
    downloadUrl: video.resultVideoUrl,
    // style-clip 전용 필드들
    videoPrompt: video.videoPrompt,
    pointCost: video.pointCost,
    sourceImageCount: video.sourceImageCount,
    synthesizedImageUrls: video.synthesizedImageUrls,
    processingStartedAt: video.processingStartedAt,
    completedAt: video.completedAt,
    errorMessage: video.errorMessage,
  }))
}

export const useStyleClipData = ({
  page,
  itemsPerPage,
  enabled = true,
}: UseStyleClipDataProps) => {
  // 비디오 쿼리
  const videoQuery = useGetGalleryVideos(page, itemsPerPage, { enabled })

  // 비디오 폴링 훅
  const { startVideoPolling, stopAllVideoPolling } = useVideoPolling({
    onVideoCompleted: (requestId, resultVideoUrl) => {
      console.log('🎉 StyleClip: Video completed', requestId, resultVideoUrl)
      videoQuery.refetch()
    },
    onVideoFailed: (requestId, errorMessage) => {
      console.log('❌ StyleClip: Video failed', requestId, errorMessage)
      videoQuery.refetch()
    },
  })

  // 데이터 변환 및 메모이제이션
  const data = useMemo((): StyleClipData => {
    if (!videoQuery?.data) {
      return {
        items: [],
        totalCount: 0,
        totalPages: 0,
      }
    }

    const videoResponse = videoQuery.data as VideoAPIResponse
    const items = transformVideoDataToStyleClipItems(videoResponse.items)

    return {
      items,
      totalCount: videoResponse.total,
      totalPages: videoResponse.totalPages,
    }
  }, [videoQuery.data])

  // processing 상태인 비디오들 폴링 시작
  useEffect(() => {
    if (enabled && videoQuery.data) {
      const videoResponse = videoQuery.data as VideoAPIResponse
      const processingVideos = videoResponse.items.filter(
        (video) => video.status === 'processing'
      )

      console.log('🔍 Found processing style-clip videos:', processingVideos.length)

      processingVideos.forEach((video) => {
        console.log('🔄 Starting polling for style-clip video ID:', video.id)
        startVideoPolling(video.id.toString())
      })
    }

    return () => {
      stopAllVideoPolling()
    }
  }, [enabled, videoQuery.data, startVideoPolling, stopAllVideoPolling])

  // 로딩 및 에러 상태
  const isLoading = videoQuery.isLoading
  const error = videoQuery.error ? (videoQuery.error as Error).message : null

  // 다운로드 함수
  const downloadItem = useCallback(async (item: StyleClipItem) => {
    try {
      if (!item.downloadUrl) {
        throw new Error('Download URL not available')
      }

      await downloadFile(item.downloadUrl, `style-clip-${item.id}.mp4`, {
        title: '스타일클립 영상',
        text: '스타일클립 영상을 저장하거나 공유하세요',
      })
    } catch (err) {
      console.error('Style clip download failed:', err)
    }
  }, [])

  // 다중 다운로드 함수
  const downloadMultiple = useCallback(
    async (items: StyleClipItem[]) => {
      const downloadableItems = items.filter(
        (item) => item.status === 'completed' && item.downloadUrl
      )

      const downloads = downloadableItems.map((item) => ({
        url: item.downloadUrl!,
        filename: `style-clip-${item.id}.mp4`,
        options: {
          title: '스타일클립 영상',
          text: '스타일클립 영상을 저장하거나 공유하세요',
        },
      }))

      await downloadMultipleFiles(downloads, 200)
    },
    []
  )

  return {
    data,
    isLoading,
    error,
    refetch: videoQuery.refetch,
    startVideoPolling,
    downloadItem,
    downloadMultiple,
  }
}